/**
 * Centralized Firestore collection name constants.
 *
 * Every Firestore collection referenced in the codebase should use one of these
 * constants instead of a hardcoded string. This makes collection renames and
 * platform forks a single-line change.
 *
 * Shared between src/ (client) and functions/src/ (server) via path alias or
 * direct import.
 */

// ── Root-level collections ───────────────────────────────────────────

export const C_OPS = '_ops';
export const C_ADMIN_USERS = 'admin_users';
export const C_AFFILIATE_PHYSICIANS = 'affiliate_physicians';
export const C_ANALYSIS_ERRORS = 'analysis_errors';
export const C_API_USAGE_LOGS = 'api_usage_logs';
export const C_CALIBRATION_CASES = 'calibration_cases';
export const C_CALIBRATION_CONVERGENCE = 'calibration_convergence';
export const C_CALIBRATION_RUNS = 'calibration_runs';
export const C_CAMPAIGNS = 'campaigns';
export const C_DATAOPS_AUDIT_LOG = 'dataops_audit_log';
export const C_DATAOPS_BREACH_EVENTS = 'dataops_breach_events';
export const C_DATAOPS_COMPLIANCE = 'dataops_compliance';
export const C_CONSENT_DOCUMENTS = 'consent_documents';
export const C_DATAOPS_CONSENT = 'dataops_consent';
export const C_DATAOPS_MINOR_DETECTIONS = 'dataops_minor_detections';
export const C_DATAOPS_REQUESTS = 'dataops_requests';
export const C_DATAOPS_RETENTION_JOBS = 'dataops_retention_jobs';
export const C_DATAOPS_RETENTION_POLICIES = 'dataops_retention_policies';
export const C_DATAOPS_THIRD_PARTY_SHARES = 'dataops_third_party_shares';
export const C_DOC_BRANDS = 'doc_brands';
export const C_DOC_CATEGORIES = 'doc_categories';
export const C_DOC_OWNERS = 'doc_owners';
export const C_DOC_SESSIONS = 'doc_sessions';
export const C_DOC_TEMPLATES = 'doc_templates';
export const C_EVENT_BUS = 'event_bus';
export const C_EVENT_CONSUMERS = 'event_consumers';
export const C_FASHION_PLATES = 'fashion_plates';
export const C_FORMULARY = 'formulary';
export const C_FUNNEL_EVENTS = 'funnel_events';
export const C_GCHAT_CONVERSATIONS = 'gchat_conversations';
export const C_GENERATION_JOBS = 'generation_jobs';
export const C_ID_MAPPINGS = 'id_mappings';
export const C_ID_MAPPING_DLQ = 'id_mapping_dlq';
export const C_MAPPING_RECONCILIATION_LOGS = 'mapping_reconciliation_logs';
export const C_INTAKE_PENDING_SUBMISSIONS = 'intake_pending_submissions';
export const C_LAB_EXTRACTIONS = 'lab_extractions';
export const C_LAB_SESSIONS = 'lab_sessions';
export const C_MAIL_QUEUE = 'mail_queue';
export const C_NURSE_CONFIGS = 'nurse_configs';
export const C_ORDERS = 'orders';
export const C_PATIENT_ANALYSES = 'patient_analyses';
export const C_PATIENT_CONSENTS = 'patient_consents';
export const C_PATIENT_EVENTS = 'patient_events';
export const C_PATIENT_JOURNEYS = 'patient_journeys';
export const C_PATIENT_PROGRAMS = 'patient_programs';
export const C_PATIENTS = 'patients';
export const C_PAYMENT_EVENTS = 'payment_events';
export const C_PHI_GATE_AUDIT = 'phi_gate_audit';
export const C_PLATFORM = 'platform';
export const C_BRANDS = 'brands';
export const C_PROMISE_KEEPER = 'promise_keeper';
export const C_REGULATORY_ALERTS = 'regulatory_alerts';
export const C_REGULATORY_CAMPAIGNS = 'regulatory_campaigns';
export const C_REGULATORY_POLICIES = 'regulatory_policies';
export const C_SAFETY = 'safety';
export const C_STAN_DIRECTIVES = 'stan_directives';
export const C_STORIES = 'stories';
export const C_STORM_FORECASTER_HISTORY = 'storm_forecaster_history';
export const C_STORM_FORECASTS_ARCHIVE = 'storm_forecasts_archive';
export const C_STORM_INSPECTIONS = 'storm_inspections';
export const C_STORM_REPORTS = 'storm_reports';
export const C_USER_PROFILES = 'user_profiles';
export const C_CONFIG_VIOLATIONS = 'config_violations';
export const C_FINGERPRINT_ANOMALIES = 'fingerprint_anomalies';
export const C_PENDING_BUILD_AUTH = 'pending_build_auth';
export const C_WEBHOOK_IDEMPOTENCY = 'webhook_idempotency';
export const C_FORECAST_PLANS = 'forecast_plans';
export const C_FAILED_JOURNEY_LINKS = 'failed_journey_links';
export const C_PRODUCTS = 'products';
export const C_PENDING_RECONCILIATION = 'pending_reconciliation';

// ── Self-Healing Alias System ────────────────────────────────────────
export const C_QUARANTINE_PATTERNS = 'quarantine_patterns';
export const C_SELF_HEALED_ALIASES = 'self_healed_aliases';

// ── Compliance Manifests (FDA De Novo evidence) ─────────────────────
export const C_COMPLIANCE_MANIFESTS = 'compliance_manifests';

// ── Vision Agreement Metrics (CAP.05 D#8) ───────────────────────────
export const C_VISION_AGREEMENT_METRICS = 'vision_agreement_metrics';

// ── Clock Synchronization Health (HLX-CAP.16) ──────────────────────
export const C_CLOCK_HEALTH = 'clock_health';

// ── Cache Health (HLX-CAP.14 Fleet Consistency Audit) ───────────────
export const C_CACHE_HEALTH = 'cache_health';
export const DOC_HEARTBEAT_MONITOR_STATUS = 'heartbeat-monitor-status';

// ── Watchdog Health (HLX-CAP.14 FD-H Watchdog — separate from cache_health) ─
export const C_WATCHDOG_HEALTH = 'watchdog_health';
export const DOC_CACHE_HEARTBEAT_WATCHDOG_STATUS = 'cache-heartbeat-watchdog-status';

// ── Unit Validation (HLX-CAP.06) ─────────────────────────────────────
export const C_UNIT_DATABASE = 'unit_database';
export const C_UNIT_VALIDATION_AUDIT = 'unit_validation_audit';

// ── Extraction Provenance ────────────────────────────────────────────
export const C_EXTRACTION_PROVENANCE = 'extraction_provenance';
export const SC_PROVENANCE_CORRECTIONS = 'corrections';

// ── PHI Temp File Lifecycle (HLX-CAP.03) ─────────────────────────────
export const C_PHI_TEMP_FILES = 'phi_temp_files';

// ── Biomarker Definitions (clinical review workflow) ─────────────────
export const C_BIOMARKER_DEFINITIONS = 'biomarker_definitions';

// ── Clinical Governance Queue ────────────────────────────────────────
export const C_CLINICAL_GOVERNANCE_QUEUE = 'clinical_governance_queue';

// ── Clinical Dataset Versioning ──────────────────────────────────────
export const C_CLINICAL_DATASETS = 'clinical_datasets';

// ── Clinical Range Change Audit Log (CAP.10 D#7 — append-only, immutable) ──
// No client write access (firestore.rules default deny-all).
// Only writeRangeChangeAuditEntry() in cap10/rangeChangeAuditService.ts writes here
// using .create() semantics — no update or delete is ever called on this collection.
export const C_CLINICAL_RANGE_AUDIT_LOG = 'clinical_range_audit_log';

// ── Gold Standard Dataset Management ────────────────────────────────
export const C_GOLD_STANDARD_DATASETS = 'gold_standard_datasets';

// ── Audit Logging System (resilient writes) ─────────────────────────
export const C_AUDIT_LOG = 'audit_log';
export const C_AUDIT_QUEUE = 'audit_queue';

// ── CAP.11 Immutable Audit Log (hallucination prevention — 7-year retention) ─
// Append-only. No client write access. Only logCap11Decision() writes here.
// Documents carry retentionUntil = write_time + 7 years — do NOT delete before.
export const C_CAP11_AUDIT_LOG = 'cap11_audit_log';

// ── CAP.11 D#8 — Real-Time Monitoring Snapshots ────────────────────────
// Written by cap11Monitor (every 5 min). Read by admin dashboard.
// Path: _ops/cap11_monitoring/{timestamp}
export const OPS_CAP11_MONITORING = 'cap11_monitoring';

// ── CAP.11 Human Review Queue (D#5.1 — case creation, routing, SLA tracking) ─
// ReviewCase documents: caseId, anonymized patientId, captainExtraction,
// validatorResult, de-identified sourceDocument (GCS doc ID from CAP.07),
// priority (HIGH/MEDIUM/LOW), reviewDeadline (4-hour SLA).
// Written by createReviewCase() in cap11/humanReviewQueueService.ts.
export const C_CAP11_REVIEW_QUEUE = 'cap11_review_queue';

// ── CAP.12 D#6 — Date Extraction Monitoring Snapshots ────────────────
// Written by cap12Monitor (every 5 min). Read by admin dashboard.
// Path: _ops/cap12_monitoring/{timestamp}
export const OPS_CAP12_MONITORING = 'cap12_monitoring';

// ── Eddie Dispute Resolution collections ────────────────────────────

export const C_EDDIE_VERDICTS = 'eddie_verdicts';
export const C_EDDIE_DISPUTES = 'eddie_disputes';

// ── Patient Critical Modifiers + Module Prompts ─────────────────────

export const C_PATIENT_CRITICAL_MODIFIERS = 'patient_critical_modifiers';
export const C_MODULE_PROMPTS = 'module_prompts';

// ── PJE (Patient Journey Engine) collections ─────────────────────────

export const C_PJE_GENERATED_PANELS = 'pje_generated_panels';
export const C_PJE_GROUND_TRUTH = 'pje_ground_truth';
export const C_PJE_LOAD_RUNS = 'pje_load_runs';
export const C_PJE_MODIFIER_FLOWS = 'pje_modifier_flows';
export const C_PJE_PANEL_COMPARISONS = 'pje_panel_comparisons';
export const C_PJE_PANELS = 'pje_panels';
export const C_PJE_PERSONAS = 'pje_personas';
export const C_PJE_PROMPT_ANALYSES = 'pje_prompt_analyses';
export const C_PJE_PROMPT_CONFIGS = 'pje_prompt_configs';
export const C_PJE_REGISTER_EVALUATIONS = 'pje_register_evaluations';
export const C_PJE_SUITE_STATS = 'pje_suite_stats';
export const C_PJE_TEST_RUNS = 'pje_test_runs';
export const C_PJE_TEST_SUITES = 'pje_test_suites';
export const C_PJE_TRAIT_PROFILES = 'pje_trait_profiles';

// ── Subcollection names ──────────────────────────────────────────────

export const SC_ACTIVE = 'active';
export const SC_CALIBRATION_SESSIONS = 'calibration_sessions';
export const SC_CLINICAL_ANALYSIS = 'clinicalAnalysis';
export const SC_DOCUMENTS = 'documents';
export const SC_FINANCIAL_SNAPSHOTS = 'financial_snapshots';
export const SC_METRICS = 'metrics';
export const SC_NOTIFICATIONS = 'notifications';
export const SC_ORDERS = 'orders';
export const SC_QBO_CONFIG = 'qbo_config';
export const SC_REFILL_TIMERS = 'refill_timers';
export const SC_STRIPE_CONFIG = 'stripe_config';
export const SC_SYNC_RULES = 'sync_rules';
export const SC_CALIBRATION = 'calibration';
export const SC_DIVERGENCE_SUMMARIES = 'divergence_summaries';
export const SC_LAUNCH_GATE = 'launch_gate';
export const SC_PJE_CONFIG = 'pje_config';
export const SC_TEST_RUNS = 'test_runs';
export const SC_STORM_ALERTS_LOG = 'log';
export const SC_MODIFIERS = 'modifiers';
export const SC_SESSION_STATE = 'session_state';
export const SC_HISTORY = 'history';

// ── Platform nested document names ──────────────────────────────────

export const DOC_CALIBRATION = 'calibration';
export const SC_CLINICAL_CALIBRATION = 'clinical_calibration';
export const SC_PROMPTS = 'prompts';

// ── Nested _ops paths ────────────────────────────────────────────────

export const OPS_ALERTS_ACTIVE = 'alerts/active';
export const OPS_COSTS_DAILY = 'costs/daily';
export const OPS_COSTS_BUDGETS_CURRENT = 'costs/budgets/current';
export const OPS_CONFIG_PRICING_CURRENT = 'config/pricing/current';
export const OPS_CONFIG_PLAYBOOK_CURRENT = 'config/playbook/current';
export const OPS_COMPLIANCE_TOS_CURRENT = 'compliance/tos-controls/current';
export const OPS_DEBT_FINDINGS = 'debt/findings';
export const OPS_DEBT_SURVEYS = 'debt/surveys';
export const OPS_DEBT_SESSIONS = 'debt/sessions';
export const OPS_DEBT_BRIEFINGS = 'debt/briefings';
export const OPS_DEPS_SNAPSHOTS_CURRENT = 'deps/snapshots/current';
export const OPS_BUILDS_SNAPSHOTS_CURRENT = 'builds/snapshots/current';
export const OPS_HEALTH_WEEKLY = 'health/weekly';
export const OPS_HEALTH_MONTHLY = 'health/monthly';
export const OPS_REMEDIATION_LOG = 'remediation/log';
export const OPS_REMEDIATION_RULES = 'remediation/rules';
export const OPS_UNSENT_MESSAGES_QUEUE = 'unsent_messages/queue';

// ── _ops document names ──────────────────────────────────────────────

export const OPS_DOC_GCHAT_HEARTBEAT = 'gchat_heartbeat';
export const OPS_DOC_SWARM_MONITOR_STATE = 'swarm_monitor_state';
export const OPS_DOC_STORM_HEARTBEAT = 'storm_heartbeat';
export const OPS_DOC_NOTIFY_HEALTH = 'notify_health';
export const OPS_DOC_NOTIFY_HMAC_SECRETS = 'notify_hmac_secrets';
export const OPS_DOC_SMS_CONFIG = 'sms_config';
export const OPS_DOC_STORM_ALERTS = 'storm_alerts';
export const OPS_DOC_FLOOR_REGISTRY = 'floor_registry';

// ── Formulary/Safety nested paths ────────────────────────────────────

export const FORMULARY_DATA_PRODUCTS = 'data';       // formulary/data/products
export const SC_PRODUCTS = 'products';
export const SC_BUNDLES = 'bundles';
export const SC_RULES = 'rules';
export const SC_MESSAGES = 'messages';
export const SC_STOCK_CASES = 'stock_cases';
export const SC_GENERIC_FORMULARY_MAP = 'generic_formulary_map';

// ── Model Accuracy Monitoring ────────────────────────────────────────

export const C_MODEL_ACCURACY_GOLDEN_DATASET = 'model_accuracy_golden_dataset';
export const OPS_MODEL_ACCURACY_DAILY = 'model_accuracy/daily';
export const OPS_MODEL_DEGRADATION_PERF_LOGS = 'model_degradation/performance_logs';
export const OPS_MODEL_DEGRADATION_PERF_SNAPSHOTS = 'model_degradation/performance_snapshots';
export const OPS_CROSS_MODEL_VALIDATION_DAILY = 'cross_model_validation/daily';
export const OPS_CROSS_MODEL_VALIDATION_ESCALATIONS = 'cross_model_validation/escalations';

// ── _ops additional paths ───────────────────────────────────────────

export const OPS_LINGUISTIC_BASELINE = 'linguistic_baseline';
export const OPS_HEALTH_DAILY = 'health/daily';
export const OPS_HEALTH_QUARTERLY = 'health/quarterly';
export const OPS_COSTS_MONTHLY = 'costs/monthly';
export const OPS_DEPS_SCANS = 'deps/scans';
export const OPS_BUILDS_HISTORY = 'builds/history';
export const OPS_ALERTS_HISTORY = 'alerts/history';
export const OPS_SMS_RATE_LIMITS = 'sms_rate_limits';

// ── Clinical Notification Service ──────────────────────────────────
export const OPS_CLINICAL_NOTIFICATIONS_PENDING = 'clinical_notifications/pending';
export const OPS_CLINICAL_NOTIFICATIONS_ESCALATED = 'clinical_notifications/escalated';

// ── Micro Longevity Messages ─────────────────────────────────────────
export const C_MICRO_LONGEVITY_MESSAGES = 'micro_longevity_messages';

// ── NAD / Sermorelin / B12 Messages ──────────────────────────────────
export const C_NAD_SERMORELIN_B12_MESSAGES = 'nad_sermorelin_b12_messages';

// ── Captain Results (CAP.17 Downstream Handoff) ─────────────────────
export const C_CAPTAIN_RESULTS = 'captain_results';
export const SC_CAPTAIN_RESULT_CORRECTIONS = 'corrections';

// ── Provider License Verification Cache ──────────────────────────────
export const C_PROVIDER_LICENSE_CACHE = 'provider_license_cache';

// ── Partner Audit Results ────────────────────────────────────────────
export const C_PARTNER_AUDITS = 'partner_audits';
export const SC_PARTNER_AUDIT_CONFIG = 'config';
export const SC_PARTNER_AUDIT_RESULTS = 'results';

// ── Partner Configuration (API access control) ──────────────────────
export const C_PARTNER_CONFIG = 'partner_config';

// ── Synthetic Data Approval (clinical sign-off + versioning) ────────
export const C_SYNTHETIC_DATA_APPROVALS = 'synthetic_data_approvals';

// ── Encounter Note Defense 2 Monitoring ────────────────────────────
export const C_ENCOUNTER_NOTES_PROCESSED = 'encounter_notes_processed';
export const OPS_DEFENSE2_MONITOR_DAILY = 'defense2_monitor/daily';

// ── Lab Date Defense 2 Monitoring ──────────────────────────────────
export const C_LAB_DEFENSE2_REVIEWS = 'lab_defense2_human_reviews';
export const OPS_LAB_DEFENSE2_DAILY = 'lab_defense2/daily';

// ── Clinical Quarantine Queue (HLX-CAP.02 D#11) ─────────────────────
export const C_CLINICAL_QUARANTINE = 'clinical_quarantine';

// ── Component Quarantine (HLX-F01) ──────────────────────────────────
export const C_COMPONENT_QUARANTINE = 'component_quarantine';

// ── Webhook Security Events (failed HMAC → SCC) ─────────────────────
export const C_WEBHOOK_SECURITY_EVENTS = 'webhook_security_events';

// ── Orphaned Appointment Rollback ────────────────────────────────────
export const C_ORPHANED_APPOINTMENTS = 'orphaned_appointments';

// ── Halted Biomarker Queue (manual review for compromised biomarkers) ─
export const C_HALTED_BIOMARKER_QUEUE = 'halted_biomarker_queue';

// ── Pending Reviewer Queue (items queued while clinical reviewer unavailable) ─
export const C_PENDING_REVIEWER_QUEUE = 'pending_reviewer_queue';

// ── Provider Eligibility Overrides (licensed provider override of automated decisions) ─
export const C_PROVIDER_ELIGIBILITY_OVERRIDES = 'provider_eligibility_overrides';

// ── Biomarker Review Queue (items awaiting clinical validation) ──────
export const C_BIOMARKER_REVIEW_QUEUE = 'biomarker_review_queue';

// ── Manual Review Queue (items requiring manual clinical review) ─────
export const C_MANUAL_REVIEW_QUEUE = 'manual_review_queue';

// ── Dead Letter Queue (items routed when biomarker processing is halted system-wide) ─
export const C_DEAD_LETTER_QUEUE = 'dead_letter_queue';

// ── Encounter DLQ (HLX-CAP.15 UC-3: webhook arrives, no patient mapping found) ─
export const C_ENCOUNTER_DLQ = 'encounter_dlq';

// ── Clinical Review Escalation State ─────────────────────────────────
export const OPS_DOC_CLINICAL_REVIEW_ESCALATION = 'clinical_review_escalation_state';

// ── Message Approval Audit Trail ─────────────────────────────────────
export const C_MESSAGE_APPROVALS = 'message_approvals';
export const C_MESSAGE_APPROVAL_AUDIT = 'message_approval_audit';

// ── Message Change Detection Audit (HLX-J06) ────────────────────────
export const C_MESSAGE_CHANGE_AUDIT = 'message_change_audit';

// ── Test Results (storm scheduler test outcomes) ────────────────────
export const C_TEST_RESULTS = 'test_results';

// ── System Health (heartbeat monitoring) ─────────────────────────────
export const C_SYSTEM_HEALTH = 'system_health';
export const DOC_RECONCILIATION_HEARTBEAT = 'reconciliation_heartbeat';

// ── Regulatory Submission Pipeline (UKA-R01) ─────────────────────────
export const C_REGULATORY_SUBMISSIONS = 'regulatory_submissions';
export const OPS_REGULATORY_CONFIG = 'regulatory_config';
export const OPS_WINTER_PROTOCOL = 'winter_protocol';

// ── Lab Upload Metadata (CAP.01 D#8.2) ───────────────────────────────
export const C_LAB_UPLOADS = 'lab_uploads';

// ── PHI Temp File Metadata (CAP.03) ─────────────────────────────────
export const C_TEMP_FILE_METADATA = 'temp_file_metadata';
export const C_PHI_ANOMALIES = 'phi_anomalies';

// ── Three-Loop Expression System (P02 + L01) ─────────────────────────
export const C_PAPER_SUBMISSIONS = 'paper_submissions';
export const C_PRIORITY_DATES = 'priority_dates';
export const C_PATENT_PACKAGES = 'patent_packages';
export const OPS_VENUE_MATRIX = 'venue_matrix';

// ── Mock Detection (CAP.09 Three-Defense Architecture) ──────────────
export const C_MOCK_DETECTION_EVENTS = 'mock_detection_events';
export const C_MOCK_DETECTION_REVIEW_QUEUE = 'mock_detection_review_queue';

// ── Patient Confirmation Gate (CAP.09 D#8 — extreme-outlier-only case) ─
// Tracks NRS confirmation requests, patient responses, and timeout escalations.
// Written by patientConfirmationGate.ts; read by NRS callback handler.
export const C_PATIENT_CONFIRMATION_GATE = 'patient_confirmation_gate';

// ── CAP.10 D#1 — Validated Range Database (append-only, immutable PLUG records) ─
// Document ID: biomarkerId (canonical lowercase, e.g. "glucose", "free_t3")
// Schema: ValidatedRange (src/types/rangeValidation.ts)
// Write rule: only writeValidatedRange() in cap10/validatedRangeSchema.ts writes here
// using .create() semantics — existing records are NEVER edited or deleted.
// Gender-split biomarkers carry standardRange.male / standardRange.female sub-objects.
// Age-dependent biomarkers carry ageDependentBrackets[] on the root document.
export const C_VALIDATED_RANGES = 'validated_ranges';

// ── Biomarker ID Contract — BIOMARKER_NOT_FOUND Events (CAP.10 D#4) ──
// Append-only. Written by biomarkerIdContract.ts whenever a rawName
// cannot be resolved to a canonical biomarkerId. Queried by the
// operational dashboard endpoint to surface alias map gaps.
export const C_BIOMARKER_NOT_FOUND_EVENTS = 'biomarker_not_found_events';

// ── CAP.10 D#9 — Data Entry Safety (HLX-F06) ────────────────────────
// Pending entries awaiting second-operator verification (step 1 → step 2).
// Documents auto-expire after 24h. Written by cap10RangeEntry, resolved by cap10RangeVerify.
export const C_CAP10_RANGE_ENTRY_PENDING = 'cap10_range_entry_pending';

// ── CAP.10 D#8 — Range Approval Workflow (HLX-F05) ──────────────────
// Dual-clinician approval records for PENDING→APPROVED transitions.
// Written by cap10SubmitForApproval; updated by cap10ApprovePrimary and cap10ApproveSecondary.
// Status lifecycle: PENDING_PRIMARY → PRIMARY_APPROVED → APPROVED | REJECTED
// NEVER written directly from client — Admin SDK only.
export const C_CAP10_RANGE_APPROVALS = 'cap10_range_approvals';

// ── Plausible Bounds Table (CAP.02 Quality Gate 2) ───────────────────
export const C_PLAUSIBLE_BOUNDS_TABLE = 'plausibleBoundsTable';

// ── Confidence Gating (HLX-CAP.13) ─────────────────────────────────
export const C_CONFIDENCE_EXCLUSION_LOGS = 'confidence_exclusion_logs';
export const C_CONFIDENCE_LOGGING_FAILURES = 'confidence_logging_failures';
// Single-document heartbeat: 'global' doc updated on every applyConfidenceGating() call.
// Used by cap13ExclusionLogMonitor to detect active processing without reading failure counters.
export const C_CONFIDENCE_PROCESSING_HEARTBEAT = 'confidence_processing_heartbeat';

// ── Pre-Launch Validation (HLX-CAP.13 D#8) ──────────────────────────
export const C_PRELAUNCH_VALIDATION = 'prelaunchValidation';

// ── Fallback Validation Queue (HLX-CAP.18) ─────────────────────────
export const C_FALLBACK_QUEUE = 'fallback_queue';
export const C_FALLBACK_EXPIRED = 'fallback_expired';
export const C_PATIENT_NOTIFICATIONS = 'patient_notifications';

// ── Lab Date Validation (HLX-CAP.12) ──────────────────────────────
export const C_DATE_VALIDATION_AUDIT = 'date_validation_audit';
export const C_PHI_VIEW_AUDIT = 'phi_view_audit';
export const C_DATE_VALIDATION_REVIEW_QUEUE = 'date_validation_review_queue';

// ── NRS.02 Age Gate (24hr IP-based cooldown) ────────────────────────
export const C_AGE_BLOCKS = 'age_blocks';

// ── Flow Bone (FLO.01-09) ────────────────────────────────────────────
export const C_PATIENT_TRENDS = 'patient_trends';
export const C_PATIENT_PROTOCOL_HISTORY = 'patient_protocol_history';
export const C_PATIENT_REFILL_STATE = 'patient_refill_state';
export const C_PATIENT_CHECK_INS = 'patient_check_ins';
export const C_FLOW_ANALYTICS = 'flow_analytics';
export const C_NOTIFICATION_TEMPLATES = 'notification_templates';
export const C_NOTIFICATION_SUPPRESSION = 'notification_suppression';
export const C_ABANDONED_SESSIONS = 'abandoned_sessions';
export const C_SESSION_RESUME_EVENTS = 'session_resume_events';
export const C_PATIENT_EXPORTS = 'patient_exports';
export const SC_CHECK_IN_RECORDS = 'records';

// ── UKA-M03: Alert Operations Architecture ──────────────────────────
export const OPS_ESCALATION_STATE = 'escalation_state';
export const OPS_ESCALATION_TIMERS = 'escalation_timers';
export const OPS_ESCALATION_HISTORY = 'escalations/history';
export const OPS_AUTO_FIX_LOG = 'auto_fix_log';
export const OPS_LAST_RESORT_LOG = 'last_resort_log';
export const OPS_DISPATCH_LOG = 'dispatch_log/entries';
export const OPS_OPERATOR_ROUTING = 'config/operator_routing';

// ── CAP.05 Human Review Queue (HLX-CAP.05 D#9a) ─────────────────────
export const C_CAP05_REVIEW_QUEUE = 'cap05_review_queue';
export const C_CAP05_DECISION_LOG = 'cap05_decision_log';

// ── CAP.05 Cross-Validation Tolerance Rules (HLX-CAP.05 D#4) ─────────
// Per-biomarker tolerance type config, managed alongside CAP.06 unit database.
// Non-technical staff update tolerance types via Admin UI; workers never touch this file.
// Document ID = canonicalId (lowercase). Fields: canonicalId, toleranceType, description.
export const C_CAP05_TOLERANCE_RULES = 'cap05_tolerance_rules';

// ── XMOD Bone (XMO.10 Unified Error Taxonomy + related specs) ───────
export const SC_CONTEXTS = 'contexts';
export const C_XMOD_CONTENT_FLAGS = 'xmod_content_flags';
export const C_XMOD_MOD_REJECTIONS = 'xmod_mod_rejections';
export const C_XMOD_ERRORS = 'xmod_errors';
export const C_XMOD_SECURITY_AUDIT = 'xmod_security_audit';
export const C_XMOD_SECURITY_STATE = 'xmod_security_state';
export const C_XMOD_SCAN_RETRY_QUEUE = 'xmod_scan_retry_queue';
export const C_XMOD_DUPLICATE_HASHES = 'xmod_duplicate_hashes';
export const C_XMOD_RETAKE_FLOWS = 'xmod_retake_flows';
export const C_XMOD_RETAKE_TRACKING = 'xmod_retake_tracking';
export const C_XMOD_GAP_ANALYSIS = 'xmod_gap_analysis';
export const C_XMOD_PLAUSIBILITY_RANGES = 'xmod_plausibility_ranges';
export const C_XMOD_ANALYST_REVIEWS = 'xmod_analyst_reviews';
export const C_XMOD_ESCALATION_QUEUE = 'xmod_escalation_queue';
export const C_XMOD_CONFIG = 'xmod_config';
export const C_XMOD_ERROR_CLASSIFICATION_RULES = 'xmod_error_classification_rules';

// ── Biomarker-Unit Association Safety (HLX-CAP.06 FD-U) ──────────────
export const C_BIOMARKER_UNIT_PENDING = 'biomarker_unit_pending';
export const C_BIOMARKER_UNIT_AUDIT = 'biomarker_unit_association_audit';
export const C_BIOMARKER_UNIT_VALID_COMBOS = 'biomarker_unit_valid_combos';
export const C_ADMIN_HARD_ERROR_QUEUE = 'admin_hard_error_queue';

// ── CAP.06 FD-O — Break-Glass Reconciliation (HLX-CAP.06 Directive #8) ──────
/** Raw Firestore writes made by the engineering break-glass account during a P1 incident */
export const C_BREAKGLASS_LOG = 'cap06_breakglass_log';
/** Per-entry reconciled records produced by cap06-breakglass-reconcile (idempotent) */
export const C_BREAKGLASS_RECONCILE_RECORDS = 'cap06_reconcile_records';
/** Top-level reconciliation reports — one per incident+window (idempotent) */
export const C_BREAKGLASS_RECONCILE_REPORTS = 'cap06_reconcile_reports';

// ── CAP.02 Biomarker Dictionary (CAP.11 D#6 — CAP.02 OWNER, CAP.11 CONSUMER) ─
// CAP.02 writes this collection. CAP.11 (and CAP.04, CAP.05) read it — NEVER write it.
// Document ID = canonicalId (lowercase, e.g. "testosterone").
// Schema: BiomarkerDictionaryEntry (functions/src/cap11/biomarkerDictionaryService.ts).
// Embedding vectors: 768-dim, L2-normalized, Vertex AI text-embedding-004.
// CAP.11 holds a real-time onSnapshot cache (Gold) or 1-hour TTL (Silver).
export const C_CAP02_BIOMARKER_DICTIONARY = 'cap02_biomarker_dictionary';
